/**
 * lib/statementDelivery.ts
 * ------------------------------------------------------------------
 * Emails a family their combined account statement.
 *
 * Unlike an invoice (a fixed document we store in Storage), a statement
 * is a point-in-time snapshot of live data, so there's nothing worth
 * persisting: we render the PDF client-side and hand it straight to the
 * sendStatementEmail Cloud Function as a base64 attachment — no Storage
 * upload, no extra Storage rule, no stale stored snapshots.
 *
 * Best-effort and non-throwing: the UI shows the returned status.
 * ---------------------------------------------------------------- */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildStatementPdf } from '@/lib/statementPdf';
import type { FamilyStatement } from '@/lib/financeCore';

export interface DeliverStatementResult {
  emailed: boolean;
  reason?: string;
}

export async function deliverStatement(
  statement: FamilyStatement,
  opts: { toEmail?: string } = {},
): Promise<DeliverStatementResult> {
  const to = (opts.toEmail ?? statement.contactEmail ?? '').trim();
  if (!to) return { emailed: false, reason: 'No contact email on file for this family.' };

  try {
    const logo = await loadLogoDataUrl();
    // datauristring → strip the "data:application/pdf;base64," prefix
    const dataUri = buildStatementPdf(statement, logo).output('datauristring');
    const pdfBase64 = dataUri.includes(',') ? dataUri.slice(dataUri.indexOf(',') + 1) : dataUri;

    const fn = httpsCallable(functions, 'sendStatementEmail');
    const res = await fn({
      toEmail: to,
      familyName: statement.familyName,
      balance: statement.balance,
      asOf: new Date().toISOString().slice(0, 10),
      pdfBase64,
    });
    const data = (res?.data ?? {}) as { ok?: boolean; reason?: string };
    return data.ok ? { emailed: true } : { emailed: false, reason: data.reason ?? 'Email not sent.' };
  } catch (e) {
    return { emailed: false, reason: (e as Error).message };
  }
}
